import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
 
function FinalInspection() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [partQty, setPartQty] = useState('1');
  const [jobNumber, setJobNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [nonConformance, setNonConformance] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [disposition, setDisposition] = useState('');
  const [saving, setSaving] = useState(false);
  const [actuals, setActuals] = useState({});
  const [statuses, setStatuses] = useState({});
 
  useEffect(() => {
    const load = async () => {
      try {
        const docRef = doc(db, 'projects', projectId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProject(data);
          setBubbles(data.bubbles || []);
          setCompanyName(data.companyName || '');
          setInspectorName(data.inspectorName || '');
          if (data.inspectionDate) setInspectionDate(data.inspectionDate);
          setActuals(data.actuals || {});
          setJobNumber(data.jobNumber || '');
          setNotes(data.notes || '');
          setNonConformance(data.nonConformance || '');
          setCorrectiveAction(data.correctiveAction || '');
          setDisposition(data.disposition || '');
          setPartQty(data.partQty || '1');
          const s = {};
          (data.bubbles || []).forEach(b => { s[b.id] = b.status || 'Pending'; });
          setStatuses(s);
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [projectId]);
 
  const setActual = (id, val) => setActuals(prev => ({ ...prev, [id]: val }));
  const setStatus = (id, val) => setStatuses(prev => ({ ...prev, [id]: val }));
 
  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedBubbles = bubbles.map(b => ({ ...b, status: statuses[b.id] || b.status }));
      await updateDoc(doc(db, 'projects', projectId), {
        companyName, inspectorName, inspectionDate,
        actuals, jobNumber, notes, partQty,
        nonConformance, correctiveAction, disposition,
        bubbles: updatedBubbles,
        finalInspectionComplete: true
      });
      alert('Final Inspection saved!');
    } catch (err) {
      console.error(err);
      alert('Error saving.');
    }
    setSaving(false);
  };
 
  const handlePrint = () => window.print();
 
  const passCount = Object.values(statuses).filter(s => s === 'Pass').length;
  const failCount = Object.values(statuses).filter(s => s === 'Fail').length;
  const naCount = Object.values(statuses).filter(s => s === 'N/A').length;
  const pendingCount = bubbles.length - passCount - failCount - naCount;
  const overallResult = failCount > 0 ? 'FAIL' : pendingCount > 0 ? 'PENDING' : bubbles.length > 0 ? 'PASS' : 'PENDING';
 
  return (
    <div style={styles.page}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br />Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.navItem} onClick={() => navigate('/')}><span>⬆</span> Upload Print</div>
          <div style={styles.navItem} onClick={() => navigate(`/review/${projectId}`)}><span>◎</span> Review Bubbles</div>
          <div style={styles.navItem} onClick={() => navigate(`/fai/${projectId}`)}><span>☰</span> FAI Document</div>
          <div style={{ ...styles.navItem, ...styles.navActive }}><span>✓</span> Final Inspection</div>
          <div style={styles.navItem} onClick={() => navigate(`/exports/${projectId}`)}><span>↓</span> Exports</div>
        </nav>
      </div>
 
      {/* MAIN */}
      <div style={styles.main}>
        <div style={styles.topbar}>
          <div style={styles.stepperRow}>
            <div style={styles.stepDone}>✓ Upload PDF</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Analyze Print</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Review Bubbles</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Generate FAI</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepActive}>5 Final Inspection</div>
          </div>
          <div style={styles.topbarRight}>
            <button style={styles.btnOutline} onClick={() => navigate(`/fai/${projectId}`)}>← Back to FAI</button>
            <button style={styles.btnOutline} onClick={handlePrint}>🖨 Print</button>
            <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Report'}</button>
          </div>
        </div>
 
        <div style={styles.content}>
 
          {/* OVERALL RESULT BANNER */}
          <div style={{
            ...styles.resultBanner,
            background: overallResult === 'PASS' ? '#dcfce7' : overallResult === 'FAIL' ? '#fef2f2' : '#fefce8',
            border: `1px solid ${overallResult === 'PASS' ? '#86efac' : overallResult === 'FAIL' ? '#fca5a5' : '#fde68a'}`
          }}>
            <div style={styles.resultLeft}>
              <div style={{
                ...styles.resultBadge,
                background: overallResult === 'PASS' ? '#16a34a' : overallResult === 'FAIL' ? '#dc2626' : '#f59e0b'
              }}>
                {overallResult === 'PASS' ? '✓' : overallResult === 'FAIL' ? '✕' : '⏳'} {overallResult}
              </div>
              <div style={styles.resultText}>
                {overallResult === 'PASS' ? 'All characteristics passed. Part is conforming.' :
                 overallResult === 'FAIL' ? `${failCount} characteristic${failCount > 1 ? 's' : ''} failed. Part is non-conforming.` :
                 `${pendingCount} characteristic${pendingCount > 1 ? 's' : ''} still pending review.`}
              </div>
            </div>
            <div style={styles.resultStats}>
              <span style={styles.statItem}><span style={{ color: '#16a34a', fontWeight: '700' }}>{passCount}</span> Pass</span>
              <span style={styles.statItem}><span style={{ color: '#dc2626', fontWeight: '700' }}>{failCount}</span> Fail</span>
              <span style={styles.statItem}><span style={{ color: '#6b7280', fontWeight: '700' }}>{naCount}</span> N/A</span>
              <span style={styles.statItem}><span style={{ color: '#f59e0b', fontWeight: '700' }}>{pendingCount}</span> Pending</span>
            </div>
          </div>
 
          {/* FINAL INSPECTION DOCUMENT */}
          <div style={styles.document} id="final-document">
 
            {/* DOCUMENT HEADER */}
            <div style={styles.docHeader}>
              <div style={styles.docHeaderLeft}>
                <input
                  style={styles.companyInput}
                  placeholder="Company Name"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                />
                <div style={styles.docReportType}>FINAL INSPECTION REPORT</div>
              </div>
              <div style={{
                ...styles.overallResult,
                background: overallResult === 'PASS' ? '#16a34a' : overallResult === 'FAIL' ? '#dc2626' : '#f59e0b'
              }}>
                {overallResult}
              </div>
            </div>
 
            {/* PART INFO */}
            <div style={styles.partInfo}>
              <div style={styles.partInfoGrid}>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Part Number</div>
                  <div style={styles.infoCellValue}>{project?.partNumber || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Revision</div>
                  <div style={styles.infoCellValue}>{project?.revision || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Material</div>
                  <div style={styles.infoCellValue}>{project?.material || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Job Number</div>
                  <input style={styles.infoInput} placeholder="Job #" value={jobNumber} onChange={e => setJobNumber(e.target.value)} />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Qty Inspected</div>
                  <input style={styles.infoInput} placeholder="1" value={partQty} onChange={e => setPartQty(e.target.value)} />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Inspector</div>
                  <input style={styles.infoInput} placeholder="Inspector name" value={inspectorName} onChange={e => setInspectorName(e.target.value)} />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Inspection Date</div>
                  <input type="date" style={styles.infoInput} value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Finish</div>
                  <div style={styles.infoCellValue}>{project?.finish || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Units</div>
                  <div style={styles.infoCellValue}>{project?.units || 'INCH'}</div>
                </div>
              </div>
            </div>
 
            {/* INSPECTION TABLE */}
            <table style={styles.table}>
              <thead>
                <tr style={styles.headRow}>
                  <th style={{ ...styles.th, width: '44px' }}>Bubble</th>
                  <th style={styles.th}>Requirement</th>
                  <th style={{ ...styles.th, width: '80px' }}>Nominal</th>
                  <th style={{ ...styles.th, width: '80px' }}>Tolerance</th>
                  <th style={{ ...styles.th, width: '110px' }}>Insp. Method</th>
                  <th style={{ ...styles.th, width: '100px' }}>Actual</th>
                  <th style={{ ...styles.th, width: '100px' }}>Result</th>
                </tr>
              </thead>
              <tbody>
                {bubbles.length === 0 ? (
                  <tr><td colSpan={7} style={styles.emptyCell}>No bubbles — go back to Review Bubbles first</td></tr>
                ) : (
                  bubbles.map((b, i) => {
                    const status = statuses[b.id] || 'Pending';
                    return (
                      <tr key={b.id} style={{ ...styles.tr, background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <div style={{
                            ...styles.bubbleNum,
                            background: status === 'Pass' ? '#16a34a' : status === 'Fail' ? '#dc2626' : '#2563EB'
                          }}>{b.number}</div>
                        </td>
                        <td style={styles.td}>{b.requirement || <span style={styles.empty}>—</span>}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', textAlign: 'center' }}>{b.nominal || '—'}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', textAlign: 'center' }}>{b.tolerance || '—'}</td>
                        <td style={{ ...styles.td, fontSize: '11px', color: '#6b7280', textAlign: 'center' }}>{b.inspectionMethod}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <input
                            style={styles.actualInput}
                            placeholder="Enter"
                            value={actuals[b.id] || ''}
                            onChange={e => setActual(b.id, e.target.value)}
                          />
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <select
                            style={{
                              ...styles.statusSelect,
                              background: status === 'Pass' ? '#dcfce7' : status === 'Fail' ? '#fef2f2' : status === 'N/A' ? '#f3f4f6' : '#fefce8',
                              color: status === 'Pass' ? '#16a34a' : status === 'Fail' ? '#dc2626' : status === 'N/A' ? '#6b7280' : '#92400e',
                              borderColor: status === 'Pass' ? '#86efac' : status === 'Fail' ? '#fca5a5' : '#e5e7eb'
                            }}
                            value={status}
                            onChange={e => setStatus(b.id, e.target.value)}
                          >
                            <option>Pending</option>
                            <option>Pass</option>
                            <option>Fail</option>
                            <option>N/A</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
 
            {/* NCR SECTION */}
            <div style={styles.notesSection}>
              <div style={styles.ncrGrid}>
                <div style={styles.ncrField}>
                  <div style={styles.notesLabel}>Notes</div>
                  <textarea
                    style={styles.notesInput}
                    placeholder="General inspection notes..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
                <div style={styles.ncrField}>
                  <div style={styles.notesLabel}>Non-Conformance</div>
                  <textarea
                    style={styles.notesInput}
                    placeholder="Describe what failed and why..."
                    value={nonConformance}
                    onChange={e => setNonConformance(e.target.value)}
                    rows={3}
                  />
                </div>
                <div style={styles.ncrField}>
                  <div style={styles.notesLabel}>Corrective Action</div>
                  <textarea
                    style={styles.notesInput}
                    placeholder="What was done to resolve the issue..."
                    value={correctiveAction}
                    onChange={e => setCorrectiveAction(e.target.value)}
                    rows={3}
                  />
                </div>
                <div style={styles.ncrField}>
                  <div style={styles.notesLabel}>Disposition</div>
                  <select
                    style={styles.dispositionSelect}
                    value={disposition}
                    onChange={e => setDisposition(e.target.value)}
                  >
                    <option value="">— Select disposition —</option>
                    <option>Use As Is</option>
                    <option>Rework</option>
                    <option>Scrap</option>
                    <option>Return to Vendor</option>
                    <option>Requires Engineering Review</option>
                  </select>
                </div>
              </div>
            </div>
 
            {/* SIGNATURE BLOCK */}
            <div style={styles.signatureBlock}>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Inspector Signature</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Date</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Quality Manager</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Date</div>
              </div>
            </div>
 
            {/* FOOTER */}
            <div style={styles.docFooter}>
              <div>Generated by MIA — Manufacturing Inspection Assistant</div>
              <div>Page 1 of 1 · {new Date().toLocaleDateString()}</div>
            </div>
          </div>
 
          {/* NEXT STEP */}
          <div style={styles.nextStep}>
            <div style={styles.nextStepText}>Ready to export? Generate your full inspection package with FAI and Final Inspection reports.</div>
            <button style={styles.btnPrimary} onClick={() => navigate(`/exports/${projectId}`)}>
              Export Package →
            </button>
          </div>
        </div>
      </div>
 
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #final-document, #final-document * { visibility: visible; }
          #final-document { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { height: '52px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 },
  stepperRow: { display: 'flex', alignItems: 'center', gap: '0', flex: 1 },
  stepDone: { fontSize: '11px', fontWeight: '500', color: '#2563EB', whiteSpace: 'nowrap' },
  stepActive: { fontSize: '11px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' },
  stepLine: { width: '24px', height: '1.5px', background: '#2563EB', margin: '0 4px' },
  topbarRight: { display: 'flex', gap: '8px' },
  btnOutline: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  btnPrimary: { background: '#2563EB', border: 'none', borderRadius: '7px', padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#fff' },
  content: { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  resultBanner: { borderRadius: '10px', padding: '14px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  resultLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  resultBadge: { color: '#fff', fontWeight: '700', fontSize: '13px', padding: '5px 12px', borderRadius: '20px', whiteSpace: 'nowrap' },
  resultText: { fontSize: '13px', color: '#374151', fontWeight: '500' },
  resultStats: { display: 'flex', gap: '20px' },
  statItem: { fontSize: '13px', color: '#374151' },
  document: { background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' },
  docHeader: { background: '#0B1220', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  docHeaderLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  companyInput: { background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: '18px', fontWeight: '700', fontFamily: 'Inter, sans-serif', outline: 'none', padding: '2px 0', width: '280px' },
  docReportType: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '4px' },
  overallResult: { color: '#fff', fontWeight: '800', fontSize: '18px', padding: '8px 20px', borderRadius: '8px', letterSpacing: '0.05em' },
  partInfo: { padding: '16px 24px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' },
  partInfoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  infoCell: { display: 'flex', flexDirection: 'column', gap: '3px' },
  infoCellLabel: { fontSize: '9.5px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  infoCellValue: { fontSize: '13px', fontWeight: '600', color: '#111827' },
  infoInput: { border: 'none', borderBottom: '1px solid #e5e7eb', background: 'transparent', fontSize: '13px', fontWeight: '600', color: '#111827', fontFamily: 'inherit', outline: 'none', padding: '2px 0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headRow: { background: '#f1f5f9' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '8px 12px', fontSize: '12.5px', color: '#374151', borderRight: '1px solid #f1f5f9', verticalAlign: 'middle' },
  bubbleNum: { width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: '#fff', margin: '0 auto' },
  actualInput: { width: '80px', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '4px 8px', fontSize: '12px', fontFamily: 'monospace', textAlign: 'center', outline: 'none', background: '#fff', color: '#111827' },
  statusSelect: { border: '1px solid', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', outline: 'none', width: '88px' },
  emptyCell: { padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' },
  empty: { color: '#d1d5db' },
  notesSection: { padding: '16px 24px', borderTop: '1px solid #e5e7eb' },
  ncrGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  ncrField: { display: 'flex', flexDirection: 'column', gap: '6px' },
  notesLabel: { fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' },
  notesInput: { width: '100%', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', color: '#374151' },
  dispositionSelect: { border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: '#374151', background: '#fff', width: '100%' },
  signatureBlock: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px', padding: '24px', borderTop: '1px solid #e5e7eb' },
  sigBox: { display: 'flex', flexDirection: 'column', gap: '6px' },
  sigLine: { height: '1px', background: '#374151', width: '100%' },
  sigLabel: { fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  docFooter: { background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af' },
  nextStep: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  nextStepText: { fontSize: '13px', color: '#1e40af', fontWeight: '500' },
};
 
export default FinalInspection;